/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useEffect, useState, useRef } from "react";
import Modal from "../../../components/ui/Modal";
import { observer } from "mobx-react-lite";
import { PhoneInput } from "../../../components/ui/Form/PhoneInput"; // Импортируем кастомный компонент
import * as yup from "yup";
import { phoneValidationRequired } from "../../../utils/validation";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Button from "../../../components/ui/Button";
import { toast } from "react-toastify";
import http from "../../../libs/http";
import { AuthResponse } from "../../../types/common";
import { isMobile } from "react-device-detect";
import appStore from "../../../stores/appStore";
import { Checkbox } from "../../../components/ui/Checkbox";
import { FormInputMask } from "../../../components/ui/Form/FormInputMask";

type LoginModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess: (data: AuthResponse) => void;
};

type AuthStage = "phone" | "code";

const RESEND_TIMEOUT = 180; // 5 минут в секундах

const LoginModal = (props: LoginModalProps) => {
  const [stage, setStage] = useState<AuthStage>("phone");
  const [isLoading, setIsLoading] = useState(false);
  const [phone, setPhone] = useState("");
  const [codeError, setCodeError] = useState("");
  const [resendTimeout, setResendTimeout] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [phoneExists, setPhoneExists] = useState<boolean | null>(null);
  const [consentRequired, setConsentRequired] = useState<boolean | null>(null);
  
  const phoneInputRef = useRef<HTMLInputElement>(null);

  const formSchema = yup.object().shape({
    phone: phoneValidationRequired,
    code:
      stage === "code"
        ? yup
            .string()
            .required("Введите код")
            .length(4, "Код должен содержать 4 цифры")
        : yup.string(),
    termsAccepted:
      stage === "code" && consentRequired === true
        ? yup.boolean().oneOf([true], "Необходимо принять условия")
        : yup.boolean(),
    messagesAccepted:
      stage === "code" && consentRequired === true
        ? yup.boolean().oneOf([true], "Необходимо принять условия")
        : yup.boolean(),
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    setError,
    clearErrors,
    watch,
    trigger,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      phone: "",
      code: "",
      termsAccepted: false,
      messagesAccepted: false,
    },
    resolver: yupResolver(formSchema),
    mode: "onChange",
  });

  const phoneValue = watch("phone");
  const codeValue = watch("code");
  const termsAccepted = watch("termsAccepted");
  const messagesAccepted = watch("messagesAccepted");

  // Проверяем, заполнены ли все необходимые поля
  const isFormValid = () => {
    if (stage === "phone") {
      return phoneValue.length === 18; // Полностью заполненный телефон
    } else {
      const hasValidCode = codeValue?.length === 4;
      if (consentRequired === true) {
        return hasValidCode && termsAccepted && messagesAccepted;
      }
      return hasValidCode;
    }
  };

  // Обработчик изменений для телефона
  const handlePhoneChange = (value: string) => {
    setValue("phone", value, { shouldValidate: true });
  };

  // Обработчик изменений для кода
  const handleCodeChange = (value: string) => {
    setValue("code", value, { shouldValidate: true });
  };

  // Таймер для повторной отправки кода
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (resendTimeout > 0) {
      timer = setTimeout(() => {
        setResendTimeout((prev) => prev - 1);
      }, 1000);
    } else if (resendTimeout === 0 && stage === "code") {
      setCanResend(true);
    }

    return () => clearTimeout(timer);
  }, [resendTimeout, stage]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const requestCode = async (phoneNumber: string) => {
    try {
      setIsLoading(true);
      setCodeError("");

      await http.post("/api/auth/request-code", {
        phone: phoneNumber,
      });

      toast.success("Код отправлен на ваш номер", {
        position: "bottom-right",
        autoClose: 300,
      });
      setStage("code");
      setResendTimeout(RESEND_TIMEOUT);
      setCanResend(false);
    } catch (error) {
      toast.error("Ошибка при отправке кода", {
        position: "bottom-right",
        autoClose: 300,
      });
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkPhoneExists = async (phoneNumber: string) => {
    try {
      const response = await http.post("/api/auth/check-phone", {
        phone: phoneNumber,
      });
      const exists: boolean = response?.data?.exists === true;
      const termsAccepted: boolean = response?.data?.terms_accepted === true;
      const messagesAccepted: boolean =
        response?.data?.messages_accepted === true;

      setPhoneExists(exists);

      // Для существующих пользователей проверяем, подтверждены ли согласия
      if (exists && (!termsAccepted || !messagesAccepted)) {
        // Пользователь существует, но согласия не подтверждены
        setConsentRequired(true);
        return { exists: true, consentRequired: true };
      } else if (exists && termsAccepted && messagesAccepted) {
        // Пользователь существует и согласия подтверждены
        setConsentRequired(false);
        return { exists: true, consentRequired: false };
      } else {
        // Пользователь не существует (нужны согласия при регистрации)
        setConsentRequired(true);
        return { exists: false, consentRequired: true };
      }
    } catch (error) {
      console.error("Ошибка проверки телефона", error);
      setPhoneExists(null);
      setConsentRequired(null);
      return null;
    }
  };

  const onSubmitPhone = async () => {
    const phoneData = getValues("phone");
    setPhone(phoneData);
    const result = await checkPhoneExists(phoneData);
    if (result?.consentRequired === true) {
      setStage("code");
    } 
      await requestCode(phoneData);
  };

  const onSubmitCode = async (data: {
    code: string;
    termsAccepted: boolean;
    messagesAccepted: boolean;
  }) => {
    try {
      setIsLoading(true);
      setCodeError("");
      clearErrors("code");

      const response = await http.post("/api/auth/verify-code", {
        phone,
        code: data.code,
        terms_accepted: data.termsAccepted,
        messages_accepted: data.messagesAccepted,
      });

      props.onAuthSuccess(response.data);
      props.onClose();
      reset();
      setStage("phone");
      await appStore.getUser();
      toast.success("Успешная авторизация", {
        position: "bottom-right",
        autoClose: 300,
      });
      window.location.reload();
    } catch (error) {
      setCodeError("Неверный код");
      setError("code", {
        type: "manual",
        message: "Неверный код",
      });
      toast.error("Неверный код подтверждения", {
        position: "bottom-right",
        autoClose: 300,
      });
      console.error("ошибка", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = stage === "phone" ? onSubmitPhone : onSubmitCode;

  const handleResendCode = async () => {
    if (canResend) {
      await requestCode(phone);
    }
  };

  // Валидируем форму при изменении чекбоксов
  useEffect(() => {
    if (stage === "code") {
      trigger(["termsAccepted", "messagesAccepted"]);
    }
  }, [termsAccepted, messagesAccepted, stage, trigger]);

  useEffect(() => {
    if (!props.isOpen) {
      setStage("phone");
      setCodeError("");
      reset();
      setResendTimeout(0);
      setCanResend(false);
      setPhoneExists(null);
      setConsentRequired(null);
    }
  }, [props.isOpen, reset]);

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} closeButton={false}>
      <h2
        className={`font-bold ${
          isMobile ? "text-xl p-4 pb-2" : "text-2xl p-4"
        }`}
      >
        Авторизация
      </h2>
      <div className={isMobile ? "w-full p-2" : "w-full p-4"}>
        {/* @ts-ignore */}
        <form onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col justify-center items-center"
        >
          {stage === "phone" ? (
            <div className={isMobile ? "w-full" : "w-full"}>
              <PhoneInput
                ref={phoneInputRef}
                name="phone"
                label="Номер телефона"
                value={phoneValue}
                onChange={handlePhoneChange}
                error={errors.phone?.message}
                className={isMobile ? "text-base" : ""}
                clearable
              />
            </div>
          ) : (
            <div className={isMobile ? "w-full" : "w-full"}>
              <div className="flex w-full gap-3 items-center">
                <div className="w-full relative">
                <FormInputMask
                   classNameForm
                    label="Код подтверждения"
                    {...register("code", {
                      required: true,
                      minLength: 4,
                      maxLength: 4,
                    })}
                    mask="0000"
                    placeholder={"____"}
                    error={errors.code?.message || codeError}
                    inputClassName={isMobile ? "text-base" : "text-lg"}
                  />
                  <div className="absolute right-0 top-0 text-sm opacity-70">
                    {resendTimeout > 0 && (
                      <span>Повтор через {formatTime(resendTimeout)}</span>
                    )}
                  </div>
                </div>
                {canResend && (
                  <Button
                    className=" border mt-[25px]"
                    onClick={handleResendCode}
                    style="outline"
                  >
                    Отправить код ещё раз
                  </Button>
                )}
              </div>
              {consentRequired === true && (
                <div id="login-agree-block" className="flex flex-col mt-4">
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(checked) => {
                      setValue("termsAccepted", checked);
                      trigger("termsAccepted");
                    }}
                    label={
                      <span>
                        Согласие с{" "}
                        <a href="/about/terms" className="border-solid border-b-1">
                          условиями использования
                        </a>{" "}
                        и{" "}
                        <a href="/about/privacy" className="border-solid border-b-1">
                          политикой конфиденциальности
                        </a>
                      </span>
                    }
                    error={errors.termsAccepted?.message}
                  />
                  <Checkbox
                    checked={messagesAccepted}
                    onChange={(checked) => {
                      setValue("messagesAccepted", checked);
                      trigger("messagesAccepted");
                    }}
                    label={"Согласие на получение сообщений от сервиса"}
                    error={errors.messagesAccepted?.message}
                  />
                </div>
              )}
            </div>
          )}
          <div className={`mt-5 w-full`}>
            <Button
              type="submit"
              style="primary"
              disabled={isLoading || !isFormValid()}
              className={isMobile ? "w-full py-3" : "w-full"}
            >
              {isLoading
                ? "Отправка..."
                : stage === "phone"
                ? "Далее"
                : "Войти"}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default observer(LoginModal);